// Scenario Simulation — React port of kula-create's scenario-simulation.html.
//
// Pedagogy: branching decision paths with consequences. Start branch must have
// id 'start'. Each decision branch presents a question + 2-3 choices, each
// pointing to the next branch via nextBranch. Endings terminate the path.
//
// Capture: ONE composite journey record — { path, endingId } — emitted when the
// learner lands on a terminal branch. Same shape as the bespoke neon-oracle
// renderer (and the registry's interaction contract): records are keyed per
// slide, so per-decision writes overwrote each other and only the last decision
// survived; the full path lives inside the single record instead.

import { useState, useCallback, useRef, useEffect } from 'react';
import { RotateCcw } from 'lucide-react';
import type { ScenarioSimulationSlideData, Theme } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { Reveal } from '../../shared/Reveal';
import { ExperienceExhibit } from '../../shared/ExperienceExhibit';
import { NeonOracleScenarioSim } from './NeonOracleScenarioSim';
import type { ActivityResponsePayload } from '../../../../../types';

interface Props {
  data: ScenarioSimulationSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: ScenarioSimulationSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

export function ScenarioSimulationSlide({ data, theme, isEditing, onUpdate, onActivityResponse, onComplete, savedResponse }: Props) {
  if (isEditing) return <BasicEditor data={data} theme={theme} onUpdate={onUpdate} />;
  // savedResponse: the bespoke neon-oracle renderer rehydrates the composite
  // journey record; the standard renderer doesn't (yet) — a returning learner
  // simply replays from the start, which is safe because the recorded journey
  // is only overwritten when a new run reaches an ending.
  //
  // Theme-world vertical slice: the neon-oracle theme gets a fully bespoke
  // futuristic-sci-fi renderer for scenario-simulation. All other themes use the
  // standard themed Student render. (Branch lives here — above any hooks — so it
  // never conditionally skips Student's hooks.) The bespoke renderer fires
  // onComplete once when the learner reaches a terminal/ending branch.
  if (theme.id === 'neon-oracle') {
    return <NeonOracleScenarioSim data={data} theme={theme} onActivityResponse={onActivityResponse} onComplete={onComplete} savedResponse={savedResponse} />;
  }
  return <Student data={data} theme={theme} onActivityResponse={onActivityResponse} onComplete={onComplete} />;
}

function Student({
  data,
  theme,
  onActivityResponse,
  onComplete,
}: {
  data: ScenarioSimulationSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const [currentBranchId, setCurrentBranchId] = useState<string>(
    data.branches.find((b) => b.id === 'start')?.id ?? data.branches[0]?.id ?? '',
  );
  const [path, setPath] = useState<Array<{ branchId: string; choiceLetter: string; pathLabel: string }>>([]);
  const completedRef = useRef(false);

  const currentBranch = data.branches.find((b) => b.id === currentBranchId);

  const choose = useCallback(
    (choice: NonNullable<ScenarioSimulationSlideData['branches'][number]['choices']>[number]) => {
      if (!currentBranch) return;
      // No capture here — records are keyed per slide, so per-choice writes
      // overwrote each other. The path accumulates locally; the single
      // composite record is written when the journey lands on an ending
      // (mirrors NeonOracleScenarioSim and the registry contract).
      setPath((prev) => [
        ...prev,
        { branchId: currentBranch.id, choiceLetter: choice.letter, pathLabel: choice.pathLabel },
      ]);
      setCurrentBranchId(choice.nextBranch);
    },
    [currentBranch],
  );

  // Replay — reset to the start so the learner can explore a different path and
  // see how other decisions play out (the point of a branching scenario). A
  // replay that reaches an ending re-captures its new journey (the completion
  // ref resets via the effect below), overwriting the record with the latest
  // complete run — never a partial one.
  const startOver = useCallback(() => {
    setPath([]);
    setCurrentBranchId(
      data.branches.find((b) => b.id === 'start')?.id ?? data.branches[0]?.id ?? '',
    );
  }, [data.branches]);

  // A choice pointing to a missing branch id, or a decision branch with no
  // choices, dead-ends the learner in a blank box (review doc §3 — reproduced
  // across themes, so an engine fault, not content). Only a genuinely broken
  // START still shows the incomplete message; a dead-end reached mid-scenario
  // falls through to a graceful terminal close below, so every path always
  // resolves to an ending. (Orphan/empty branches should also be caught at
  // generation time — see interactionRegistry scenario-simulation qaRejectRules.)
  const isDeadEndDecision =
    currentBranch?.type === 'decision' && (currentBranch.choices?.length ?? 0) === 0;

  const isBrokenStart = !currentBranch && path.length === 0;

  // Render the ending layout when we're at a real ending, or when the path has
  // dead-ended (missing branch / empty decision). Fall back gracefully so the
  // learner always lands on a close rather than a blank box.
  const renderEnding =
    !isBrokenStart && (!currentBranch || currentBranch.type === 'ending' || isDeadEndDecision);

  // Fire onComplete exactly once when the learner reaches any terminal state
  // (real ending or graceful dead-end close). A broken start is not a
  // completion. The same guard writes the ONE composite journey record — the
  // full path plus where it landed — matching the neon-oracle renderer and the
  // registry's responseSchema. Restart resets the ref so a replay re-captures
  // its new journey.
  useEffect(() => {
    if (renderEnding && !completedRef.current) {
      completedRef.current = true;
      onActivityResponse?.({
        activityType: 'scenario-simulation',
        mode: 'quiz',
        responseData: { path, endingId: currentBranchId },
      });
      onComplete?.();
    }
    if (!renderEnding) completedRef.current = false;
  }, [renderEnding, onComplete, onActivityResponse, path, currentBranchId]);

  if (isBrokenStart) {
    return (
      <div
        className="h-full flex items-center justify-center text-sm"
        style={{ background: theme.colors.background, color: theme.colors.textMuted }}
      >
        No starting branch — the scenario is incomplete.
      </div>
    );
  }

  const endingTitle = currentBranch?.title ?? 'Where that path led';
  // The outcome (what happened) and the lesson (why it matters) are SEPARATE
  // fields — show both. The consequence is the teaching payoff; folding it into
  // the same `??` chain as the outcome silently dropped the actual learning.
  const endingOutcome = currentBranch?.situation ?? currentBranch?.description;
  const endingLesson = currentBranch?.consequence;
  const endingReflection = currentBranch?.reflection;
  // Never render an empty close: if a branch carries no prose at all, fall back
  // to a plain line so the learner still gets a clear terminal beat.
  const endingFallback =
    !endingOutcome && !endingLesson && !endingReflection
      ? 'You’ve reached the end of this path.'
      : undefined;

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      {/* Decision trail — sticky strip showing the path the learner has
          actually taken: one numbered pill per decision made (labelled with
          their choice), then a "deciding" pill for the beat in front of them.
          Branching scenarios have no fixed length, so we never show phantom
          upcoming beats — only real history. Appears once the first choice is
          made, so the opening question isn't crowded. Gives the Reflect & Act
          closer concrete material to debrief. */}
      {path.length > 0 && (
        <div
          /* top-12 clears the lesson viewer's 48px floating nav band (progress
             dots + back arrow live at top-0); top-0 here collided with them.
             Background is near-opaque so scrolled content doesn't bleed through. */
          className="sticky top-12 z-10 backdrop-blur"
          style={{
            background: `color-mix(in srgb, ${theme.colors.background} 97%, transparent)`,
            borderBottom: `1px solid ${theme.colors.textMuted}22`,
          }}
        >
          <div className="mx-auto max-w-[760px] px-6 py-2 flex items-center gap-2 flex-wrap">
            <span
              className="font-mono uppercase tracking-wider"
              style={{ color: theme.colors.textMuted, fontSize: 10 }}
            >
              your path
            </span>
            {path.map((p, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs"
                style={{
                  border: `1px solid ${theme.colors.textMuted}33`,
                  background: theme.colors.surface,
                  color: theme.colors.text,
                }}
              >
                <span style={{ fontWeight: 600 }}>{i + 1}</span>
                <span style={{ opacity: 0.5 }}>·</span>
                <span>{p.pathLabel}</span>
              </span>
            ))}
            {!renderEnding && currentBranch?.type === 'decision' && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs"
                style={{ border: `1px solid ${accent}`, background: `${accent}14`, color: accent }}
              >
                <span style={{ fontWeight: 600 }}>{path.length + 1}</span>
                <span style={{ opacity: 0.5 }}>·</span>
                <span>deciding</span>
              </span>
            )}
          </div>
        </div>
      )}

      <div className="mx-auto max-w-[760px] px-6 py-10">
        <header className="text-center mb-6">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>}
        </header>

        {/* Each beat fades in on mount. The Reveal is keyed by branch id so
            moving to the next decision (or an ending) remounts and re-animates,
            reading as progression. NB: do NOT wrap this in <AnimatePresence
            mode="wait"> — with a custom child component the exit never signals
            completion, so the next branch never mounts and the scenario freezes
            on an invisible card. */}
        <Reveal key={currentBranchId} variant="fade">
          {!renderEnding && currentBranch ? (
              <div className="rounded-lg p-6 border" style={{ background: theme.colors.surface, borderColor: `${accent}30` }}>
                {currentBranch.label && (
                  <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
                    {currentBranch.label}
                  </p>
                )}
                {/* The exhibit — the artifact the decisions revolve around,
                    shown in full at the entry branch (before the first choice)
                    so no later question refers to something never seen. */}
                {data.exhibit && path.length === 0 && (
                  <ExperienceExhibit exhibit={data.exhibit} theme={theme} />
                )}
                {currentBranch.situation && (
                  <p className="text-sm leading-relaxed mb-3" style={{ color: theme.colors.text }}>
                    {currentBranch.situation}
                  </p>
                )}
                {currentBranch.consequence && (
                  <div
                    className="rounded p-3 mb-3 text-sm italic"
                    style={{ background: `${accent}10`, color: theme.colors.textMuted }}
                  >
                    {currentBranch.consequence}
                  </div>
                )}
                {currentBranch.question && (
                  <p className="text-base font-semibold mb-4" style={{ color: theme.colors.text }}>
                    {currentBranch.question}
                  </p>
                )}
                <div className="space-y-2">
                  {(currentBranch.choices ?? []).map((choice) => (
                    <button
                      key={choice.letter}
                      onClick={() => choose(choice)}
                      className="w-full text-left rounded-lg p-3 border-2 transition-colors"
                      style={{
                        background: theme.colors.background,
                        borderColor: `${theme.colors.textMuted}33`,
                        color: theme.colors.text,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = accent)}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = `${theme.colors.textMuted}33`)}
                    >
                      <span className="font-bold mr-2" style={{ color: accent }}>{choice.letter}.</span>
                      {choice.text}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              // ending — payoff register: heavier accent rule + larger radius +
              // shadow. Shows the outcome (what happened) AND the lesson (why it
              // matters), then offers a replay so the learner can try another
              // path. Safe fallbacks keep a sparse/broken branch from rendering
              // a blank box.
              <div
                className="border-l-[5px] rounded-r-xl p-6 shadow-sm"
                style={{ background: theme.colors.surface, borderColor: accent }}
              >
                <h3 className="font-bold text-lg mb-3" style={{ color: accent }}>{endingTitle}</h3>
                {endingOutcome && (
                  <p className="text-sm leading-relaxed mb-4" style={{ color: theme.colors.text }}>
                    {endingOutcome}
                  </p>
                )}
                {endingLesson && (
                  <div className="rounded p-3 mb-4 text-sm leading-relaxed" style={{ background: `${accent}10`, color: theme.colors.text }}>
                    <p className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: accent }}>What this shows</p>
                    {endingLesson}
                  </div>
                )}
                {endingReflection && (
                  <div className="rounded p-3 mb-4 text-sm" style={{ background: `${accent}15`, color: theme.colors.textMuted }}>
                    <p className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: accent }}>Reflect</p>
                    {endingReflection}
                  </div>
                )}
                {endingFallback && (
                  <p className="text-sm leading-relaxed mb-4" style={{ color: theme.colors.text }}>
                    {endingFallback}
                  </p>
                )}
                <button
                  onClick={startOver}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors"
                  style={{ border: `1px solid ${accent}`, color: accent, background: 'transparent' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${accent}12`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <RotateCcw className="w-4 h-4" />
                  Try another path
                </button>
              </div>
            )}
        </Reveal>
      </div>
    </div>
  );
}

function BasicEditor({
  data,
  theme,
  onUpdate,
}: {
  data: ScenarioSimulationSlideData;
  theme: Theme;
  onUpdate: (data: ScenarioSimulationSlideData) => void;
}) {
  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[720px] space-y-3">
        <input
          value={data.title}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          placeholder="Title…"
          className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => onUpdate({ ...data, subtitle: e.target.value })}
          placeholder="Subtitle"
          className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
          style={{ color: theme.colors.textMuted }}
        />
        <div className="rounded p-3 text-xs" style={{ background: `${theme.colors.textMuted}11`, color: theme.colors.textMuted }}>
          {data.branches.filter((b) => b.type === 'decision').length} decision branch(es) · {data.branches.filter((b) => b.type === 'ending').length} ending(s).
          Structural edits (branches, choices, paths) AI-only — regenerate via AI build.
        </div>
      </div>
    </div>
  );
}
